import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { applyActionCode } from 'firebase/auth';
import { auth } from '../../firebase';
import { useAuth } from '../../contexts/AuthContext';

const EmailVerificationModal = ({ onClose }) => {
  const { currentUser, verifyUserEmail } = useAuth();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('loading');
  const [message, setMessage] = useState('');

  useEffect(() => {
    handleEmailVerification();
  }, []); // Run once on mount

  const handleEmailVerification = async () => {
    setStatus('loading');
    setMessage('Verifying your email address...');
    
    try {
      const oobCode = searchParams.get('oobCode');
      console.log('Starting verification with oobCode:', oobCode ? 'present' : 'missing');
      
      if (!oobCode) {
        console.error('No oobCode found in URL parameters');
        setStatus('error');
        setMessage('Invalid verification link - missing verification code.');
        return;
      }

      console.log('Attempting Firebase Auth verification...');
      // First, apply the action code to verify with Firebase Auth
      await applyActionCode(auth, oobCode);
      console.log('✅ Firebase Auth verification completed successfully');
      
      // Set success immediately after Firebase verification succeeds
      setStatus('success');
      setMessage('Your email has been verified successfully! You can now access all features.');
      
      // Update custom status in the background (non-blocking)
      setTimeout(async () => {
        let attempts = 0;
        const maxAttempts = 10;
        
        const updateCustomStatus = async () => {
          attempts++;
          console.log(`Attempt ${attempts} to update custom verification status, currentUser:`, currentUser ? 'present' : 'null');
          
          if (currentUser) {
            try {
              await verifyUserEmail();
              console.log('✅ Custom verification status updated successfully');
            } catch (verifyError) {
              console.error('❌ Error updating custom verification status:', verifyError);
            }
          } else if (attempts < maxAttempts) {
            // Wait a bit more for auth state to update
            setTimeout(updateCustomStatus, 500);
          } else {
            console.log('❌ Max attempts reached, giving up on custom status update');
          }
        };
        
        updateCustomStatus();
      }, 100); // Start checking almost immediately
      
    } catch (error) {
      console.error('❌ Error during verification process:', error);
      
      // If the error is invalid-action-code, it might mean the code was already used successfully
      // Let's check if the verification actually worked despite the error
      if (error.code === 'auth/invalid-action-code') {
        console.log('Checking if verification was already completed by another process...');
        
        // Wait a moment and check if the user is now verified
        setTimeout(async () => {
          try {
            // Reload the current user to get latest verification status
            await auth.currentUser?.reload();
            
            if (auth.currentUser?.emailVerified) {
              console.log('✅ Email was actually verified successfully!');
              setStatus('success');
              setMessage('Your email has been verified successfully! You can now access all features.');
              
              // Update custom status
              if (currentUser) {
                try {
                  await verifyUserEmail();
                  console.log('✅ Custom verification status updated');
                } catch (customError) {
                  console.error('Error updating custom status:', customError);
                }
              }
            } else {
              setStatus('error');
              setMessage('This verification link is invalid or has expired. Please request a new verification email.');
            }
          } catch (checkError) {
            console.error('Error checking verification status:', checkError);
            setStatus('error');
            setMessage('This verification link is invalid or has expired. Please request a new verification email.');
          }
        }, 1000);
      } else if (error.code === 'auth/expired-action-code') {
        setStatus('error');
        setMessage('This verification link has expired. Please request a new verification email.');
      } else {
        setStatus('error');
        setMessage(`Failed to verify email: ${error.message}`);
      }
    }
  };

  const handleClose = () => {
    // Clean up URL parameters
    const newUrl = new URL(window.location);
    newUrl.searchParams.delete('mode');
    newUrl.searchParams.delete('oobCode');
    newUrl.searchParams.delete('apiKey');
    newUrl.searchParams.delete('continueUrl');
    newUrl.searchParams.delete('lang');
    window.history.replaceState(null, '', newUrl.toString());
    
    onClose();
  };

  const getStatusIcon = () => {
    switch (status) {
      case 'success':
        return '✅';
      case 'error':
        return '❌';
      default:
        return '⏳';
    }
  };

  const getStatusColor = () => {
    switch (status) {
      case 'success':
        return '#059669';
      case 'error':
        return '#dc2626';
      default:
        return '#6b7280';
    }
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: '20px'
    }}>
      <div style={{
        backgroundColor: 'white',
        borderRadius: '12px',
        padding: '40px',
        maxWidth: '500px',
        width: '100%',
        textAlign: 'center',
        boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
      }}>
        <div style={{
          fontSize: '48px',
          marginBottom: '20px'
        }}>
          {getStatusIcon()}
        </div>

        <h2 style={{
          fontSize: '24px',
          fontWeight: 'bold',
          color: '#1f2937',
          marginBottom: '16px'
        }}>
          Email Verification
        </h2>

        <p style={{
          fontSize: '16px',
          color: getStatusColor(),
          lineHeight: '1.6',
          marginBottom: '30px'
        }}>
          {message || 'Processing email verification...'}
        </p>

        {status === 'success' && (
          <div style={{
            backgroundColor: '#f0fdf4',
            border: '1px solid #10b981',
            borderRadius: '8px',
            padding: '16px',
            marginBottom: '20px',
            textAlign: 'left'
          }}>
            <h3 style={{ color: '#047857', margin: '0 0 8px 0', fontSize: '16px' }}>
              🎉 Welcome to Practice Talking!
            </h3>
            <ul style={{ color: '#047857', margin: 0, fontSize: '14px', paddingLeft: '20px' }}>
              <li>Book practice sessions with expert providers</li>
              <li>Become a provider and help others practice</li>
              <li>Access all premium features</li>
            </ul>
          </div>
        )}

        <button
          onClick={handleClose}
          style={{
            backgroundColor: status === 'success' ? '#059669' : '#6b7280',
            color: 'white',
            border: 'none',
            borderRadius: '6px',
            padding: '12px 24px',
            fontSize: '16px',
            fontWeight: '600',
            cursor: 'pointer',
            minWidth: '120px'
          }}
        >
          {status === 'success' ? 'Continue' : 'Close'}
        </button>
      </div>
    </div>
  );
};

export default EmailVerificationModal;